import { clamp, cos, sin } from './dmath.ts';
import { BARRIER, FROST, HASTE, L, METEOR, SKILL_COOLDOWN, SKILL_IDS, SKILL_PICK, TARGETED, KINDS, deckOf, type Kind, type SkillId } from './config.ts';
import { DT, type Ctx } from './context.ts';
import { damage } from './combat.ts';

const dist = (ax: number, az: number, bx: number, bz: number) => Math.sqrt((ax - bx) * (ax - bx) + (az - bz) * (az - bz));

/** Where a skill aimed at (x, z) actually lands: from where enemies have formed in the fog to just short of the gate. */
export function skillTarget(skill: SkillId, x: number, z: number) {
  return { x: clamp(x, L.revealEndX, 2.4), z: skill === 'barrier' ? L.roadZ : clamp(z, L.roadZ - 0.9, L.roadZ + 0.9) };
}
/** Whether a point is on the road, where a targeted skill may be cast. */
export const onRoad = (x: number, z: number) => Math.abs(z - L.roadZ) <= L.roadDepth / 2 + 0.5 && x >= L.revealEndX - 0.4 && x <= L.gateX;

/** The lobby's pick, taken in the match's opening seconds only (one active skill: a pick, not a swap). */
export function equipSkill(ctx: Ctx, player: number, skill: SkillId) {
  const p = ctx.st.players[player];
  if (ctx.st.clock >= SKILL_PICK.window || skill === p.skill) return;
  p.skill = skill;
  ctx.ev.push({ type: 'skill_equipped', player, skill });
}

/** The lobby's deck (four spheres) and their mastery, taken in the match's opening seconds only. */
export function setDeck(ctx: Ctx, player: number, kinds: readonly Kind[], mastery?: readonly number[]) {
  if (ctx.st.clock >= SKILL_PICK.window) return;
  const p = ctx.st.players[player], deck = deckOf(kinds);
  if (mastery) kinds.forEach((k, i) => { p.mastery[KINDS.indexOf(k)] = mastery[i]; });
  if (deck.join() === p.deck.join()) return;
  p.deck = deck;
  ctx.ev.push({ type: 'deck_set', player, deck: [...deck] });
}

export function cast(ctx: Ctx, player: number, skill: SkillId, x: number, z: number) {
  const { st } = ctx, p = st.players[player], index = SKILL_IDS.indexOf(skill);
  if (index >= 0 && skill !== p.skill) { ctx.ev.push({ type: 'skill_denied', player, skill, reason: 'not_equipped' }); return; }
  if (index < 0 || p.cooldown[index] > 0 || (TARGETED[skill] && !onRoad(x, z))) {
    ctx.ev.push({ type: 'skill_denied', player, skill, reason: index >= 0 && p.cooldown[index] > 0 ? 'cooldown' : 'target' });
    return;
  }
  p.cooldown[index] = SKILL_COOLDOWN[skill];
  if (skill === 'haste') {
    p.hasteT = HASTE.time;
    ctx.ev.push({ type: 'skill', player, skill, x: 0, z: 0 });
    return;
  }
  const at = skillTarget(skill, x, z);
  if (skill === 'frost') {
    // a freezing wave rolls out from the chosen point and freezes whoever it reaches
    p.wave = { x: at.x, z: at.z, t: 0, reached: [] };
  } else if (skill === 'meteor') {
    p.burn = { x: at.x, z: at.z, radius: METEOR.burnRadius, remaining: 0 };
    p.meteors.length = 0;
    for (let i = 0; i < METEOR.count; i++) {
      const a = i * 2.39996, r = i ? 0.35 + (i % 3) * 0.27 : 0;
      p.meteors.push({ x: at.x + cos(a) * r, z: at.z + sin(a) * r * 0.7, time: -i * METEOR.stagger });
    }
  } else {
    const b = st.barrier = { id: ++st.barrierSerial, owner: player, x: at.x, z: L.roadZ, hp: BARRIER.hp, maxHp: BARRIER.hp, age: 0, remaining: BARRIER.life };
    for (const e of st.enemies) if (e.crowdX > b.x - e.radius - 0.1) e.barrierPass = b.id;
  }
  ctx.ev.push({ type: 'skill', player, skill, x: at.x, z: at.z });
}

export function stepAbilities(ctx: Ctx) {
  const { st } = ctx, dt = DT;
  st.players.forEach((p, player) => {
    for (let i = 0; i < p.cooldown.length; i++) p.cooldown[i] = Math.max(0, p.cooldown[i] - dt);
    if (p.wave) {
      const w = p.wave; w.t += dt;
      const r = Math.min(1, w.t / FROST.spread) * FROST.radius;
      for (const e of st.enemies) {
        if (!e.alive || w.reached.includes(e.id) || dist(e.crowdX, e.crowdZ, w.x, w.z) > r + e.radius) continue;
        w.reached.push(e.id);
        // the same frozen state for everyone: soldiers, runners and bosses alike
        e.frozen = Math.max(e.frozen, FROST.freeze); e.hop = 0; e.taunt = 0; e.attacking = 0;
        ctx.ev.push({ type: 'freeze', id: e.id });
      }
      if (w.t >= FROST.spread) p.wave = null;
    }
    p.hasteT = Math.max(0, p.hasteT - dt);
    for (let i = p.meteors.length - 1; i >= 0; i--) {
      const m = p.meteors[i]; m.time += dt;
      if (m.time < METEOR.fall) continue;
      ctx.ev.push({ type: 'meteor_impact', player, x: m.x, z: m.z });
      for (const e of st.enemies) {
        if (e.alive && dist(e.crowdX, e.crowdZ, m.x, m.z) < METEOR.radius + e.radius) damage(ctx, e, METEOR.damage, 'r', true, player);
      }
      p.meteors.splice(i, 1);
      if (p.burn) p.burn.remaining = METEOR.burnTime;
    }
    if (p.burn && p.burn.remaining > 0) {
      const b = p.burn; b.remaining = Math.max(0, b.remaining - dt);
      for (const e of st.enemies) if (e.alive && dist(e.crowdX, e.crowdZ, b.x, b.z) < b.radius) damage(ctx, e, METEOR.burnDps * dt, 'r', false, player);
      if (b.remaining === 0) p.burn = null;
    }
  });
  const b = st.barrier;
  if (b) {
    b.age += dt; b.remaining -= dt;
    if (b.hp <= 0 || b.remaining <= 0) {
      ctx.ev.push({ type: 'barrier_end', broken: b.hp <= 0, x: b.x, z: b.z });
      st.barrier = null;
    }
  }
}
